import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, X, Users, Package, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice } from "../lib/api";

const EMPTY = {
  name_es: "", name_en: "", description_es: "", description_en: "",
  price: 0, category: "collares", images: [], materials_es: "", materials_en: "",
  featured: false, in_stock: true,
};
const CATS = ["collares", "aretes", "pulseras", "anillos"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [view, setView] = useState("products");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [imagesText, setImagesText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/auth/me").then(load).catch(() => navigate("/admin/login"));
  }, [navigate, load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setImagesText("");
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm(p);
    setImagesText((p.images || []).join("\n"));
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price),
      images: imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/products/${editing}`, payload);
        toast.success("Producto actualizado");
      } else {
        await api.post("/products", payload);
        toast.success("Producto creado");
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Producto eliminado");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const logout = () => {
    localStorage.removeItem("pas_token");
    navigate("/admin/login");
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-white">
      <div className="border-b border-canvas-deep">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl text-ink">Pasionaria · Admin</h1>
            <p className="text-ink-soft text-sm">{products.length} productos · {subscribers.length} suscriptores</p>
          </div>
          <div className="flex gap-3">
            {view === "products" && (
              <button data-testid="admin-add-btn" onClick={openNew} className="bg-brand-primary text-white px-5 py-2.5 text-sm uppercase tracking-wide hover:bg-brand-dark transition-colors flex items-center gap-2">
                <Plus size={16} /> Nuevo
              </button>
            )}
            <button data-testid="admin-logout-btn" onClick={logout} className="border border-brand-secondary px-4 py-2.5 text-sm hover:bg-canvas-soft transition-colors flex items-center gap-2">
              <LogOut size={16} /> Salir
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 flex gap-1 -mb-px">
          <button
            data-testid="tab-products"
            onClick={() => setView("products")}
            className={`px-5 py-3 text-sm flex items-center gap-2 border-b-2 transition-colors ${view === "products" ? "border-brand-primary text-brand-primary" : "border-transparent text-ink-soft hover:text-ink"}`}
          >
            <Package size={16} /> Productos
          </button>
          <button
            data-testid="tab-subscribers"
            onClick={() => setView("subscribers")}
            className={`px-5 py-3 text-sm flex items-center gap-2 border-b-2 transition-colors ${view === "subscribers" ? "border-brand-primary text-brand-primary" : "border-transparent text-ink-soft hover:text-ink"}`}
          >
            <Users size={16} /> Suscriptores
          </button>
        </div>
      </div>

      {view === "products" ? (
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-canvas-deep text-xs uppercase tracking-wide text-ink-soft">
                <th className="py-3 pr-4">Imagen</th>
                <th className="py-3 pr-4">Nombre</th>
                <th className="py-3 pr-4">Categoría</th>
                <th className="py-3 pr-4">Precio</th>
                <th className="py-3 pr-4">Destacado</th>
                <th className="py-3 pr-4">Stock</th>
                <th className="py-3"></th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} data-testid={`admin-row-${p.id}`} className="border-b border-canvas-deep/60">
                  <td className="py-3 pr-4">
                    <img src={p.images?.[0]} alt="" className="w-12 h-14 object-cover bg-canvas-soft" />
                  </td>
                  <td className="py-3 pr-4 text-ink">{p.name_es}</td>
                  <td className="py-3 pr-4 text-ink-soft capitalize">{p.category}</td>
                  <td className="py-3 pr-4 text-ink">{formatPrice(p.price)}</td>
                  <td className="py-3 pr-4">{p.featured ? "★" : "—"}</td>
                  <td className="py-3 pr-4">{p.in_stock ? "✓" : "✕"}</td>
                  <td className="py-3 flex gap-2">
                    <button data-testid={`admin-edit-${p.id}`} onClick={() => openEdit(p)} className="p-2 hover:bg-canvas-soft text-ink-soft hover:text-brand-primary"><Pencil size={16} /></button>
                    <button data-testid={`admin-delete-${p.id}`} onClick={() => handleDelete(p.id)} className="p-2 hover:bg-canvas-soft text-ink-soft hover:text-destructive"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      ) : (
        <div data-testid="subscribers-view" className="max-w-6xl mx-auto px-6 py-8">
          {subscribers.length === 0 ? (
            <div className="text-center py-20 text-ink-soft flex flex-col items-center gap-3">
              <Mail size={40} strokeWidth={1.2} className="text-brand-secondary" />
              <p>Aún no hay suscriptores.</p>
            </div>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-canvas-deep text-xs uppercase tracking-wide text-ink-soft">
                  <th className="py-3 pr-4">Correo</th>
                  <th className="py-3 pr-4">Cupón</th>
                  <th className="py-3 pr-4">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((s) => (
                  <tr key={s.id} data-testid={`subscriber-row-${s.id}`} className="border-b border-canvas-deep/60">
                    <td className="py-3 pr-4 text-ink">{s.email}</td>
                    <td className="py-3 pr-4 text-brand-primary font-medium">{s.coupon_code}</td>
                    <td className="py-3 pr-4 text-ink-soft text-sm">{(s.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-ink/50 z-[80] flex items-center justify-center p-4 overflow-y-auto">
          <div data-testid="admin-form-modal" className="bg-white w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-canvas-deep sticky top-0 bg-white">
              <h2 className="font-heading text-2xl text-ink">{editing ? "Editar producto" : "Nuevo producto"}</h2>
              <button onClick={() => setShowForm(false)} data-testid="admin-form-close"><X size={22} className="text-ink-soft" /></button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nombre (ES)" testid="field-name-es" value={form.name_es} onChange={(v) => setField("name_es", v)} required />
                <Field label="Nombre (EN)" testid="field-name-en" value={form.name_en} onChange={(v) => setField("name_en", v)} required />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Descripción (ES)</Label>
                  <textarea data-testid="field-desc-es" value={form.description_es} onChange={(e) => setField("description_es", e.target.value)} rows={3} className={inputCls} />
                </div>
                <div>
                  <Label>Descripción (EN)</Label>
                  <textarea data-testid="field-desc-en" value={form.description_en} onChange={(e) => setField("description_en", e.target.value)} rows={3} className={inputCls} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Precio (COP)" testid="field-price" type="number" value={form.price} onChange={(v) => setField("price", v)} required />
                <div>
                  <Label>Categoría</Label>
                  <select data-testid="field-category" value={form.category} onChange={(e) => setField("category", e.target.value)} className={inputCls}>
                    {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Materiales (ES)" testid="field-mat-es" value={form.materials_es} onChange={(v) => setField("materials_es", v)} />
                <Field label="Materiales (EN)" testid="field-mat-en" value={form.materials_en} onChange={(v) => setField("materials_en", v)} />
              </div>
              <div>
                <Label>Imágenes (una URL por línea)</Label>
                <textarea data-testid="field-images" value={imagesText} onChange={(e) => setImagesText(e.target.value)} rows={3} placeholder="https://..." className={inputCls} />
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input data-testid="field-featured" type="checkbox" checked={form.featured} onChange={(e) => setField("featured", e.target.checked)} /> Destacado
                </label>
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input data-testid="field-instock" type="checkbox" checked={form.in_stock} onChange={(e) => setField("in_stock", e.target.checked)} /> En stock
                </label>
              </div>
              <div className="flex gap-3 pt-2">
                <button data-testid="admin-save-btn" type="submit" disabled={saving} className="bg-brand-primary text-white px-8 py-3 text-sm uppercase tracking-wide hover:bg-brand-dark transition-colors disabled:opacity-50">
                  {saving ? "..." : "Guardar"}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="border border-brand-secondary px-8 py-3 text-sm hover:bg-canvas-soft transition-colors">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full border border-canvas-deep bg-white px-3 py-2 focus:border-brand-primary focus:outline-none text-ink text-sm";
const Label = ({ children }) => <label className="text-xs uppercase tracking-wide text-ink-soft block mb-1">{children}</label>;
const Field = ({ label, value, onChange, testid, type = "text", required }) => (
  <div>
    <Label>{label}</Label>
    <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className={inputCls} />
  </div>
);
